import { z } from 'zod';

/**
 * People: the shapes the people screens and the identity API share.
 *
 * Kept here, rather than restated on each side, because both sides used to
 * carry their own copy and the copies drifted: the API once offered an agent
 * type the database's check constraint refused, and the screen's list of types
 * was a third, hand-typed copy that nothing compared with the other two.
 */

// ---------------------------------------------------------------------------
// Agents
// ---------------------------------------------------------------------------

/** The agent types the `agents` table accepts (its `agents_type_valid` check). */
export const AGENT_TYPES = ['full_time', 'occasional'] as const;
export const AgentTypeSchema = z.enum(AGENT_TYPES);
export type AgentType = z.infer<typeof AgentTypeSchema>;

// ---------------------------------------------------------------------------
// Requester groups
// ---------------------------------------------------------------------------

/**
 * `manual`: members are chosen by hand. `rule`: members are whoever matches the
 * group's rule, kept up to date as people's details change.
 */
export const REQUESTER_GROUP_KINDS = ['manual', 'rule'] as const;
export const RequesterGroupKindSchema = z.enum(REQUESTER_GROUP_KINDS);
export type RequesterGroupKind = z.infer<typeof RequesterGroupKindSchema>;

/** The person attributes a rule can match on — each one a column every account has. */
export const REQUESTER_RULE_ATTRIBUTES = [
  'department',
  'location',
  'job_title',
  'email_domain',
  'vip',
  'active',
] as const;
export type RequesterRuleAttribute = (typeof REQUESTER_RULE_ATTRIBUTES)[number];

export const REQUESTER_RULE_LIMITS = {
  /** Conditions in one rule. */
  conditions: 20,
  /** Departments or locations in one condition. */
  choices: 100,
  /** Email domains in one condition. */
  domains: 50,
} as const;

const ReferenceListSchema = z
  .array(z.coerce.number().int().positive())
  .min(1, 'Choose at least one')
  .max(REQUESTER_RULE_LIMITS.choices);

/**
 * An email domain as people type it: `example.com`. A leading `@` is dropped,
 * so `@example.com` means the same; an address or a bare word is refused,
 * because it could never match anybody.
 */
export const EmailDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((value) => value.replace(/^@/, ''))
  .pipe(
    z
      .string()
      .min(3, 'Enter a domain such as example.com')
      .max(253)
      .regex(/^[^\s@,.]+(\.[^\s@,.]+)+$/, 'Enter a domain such as example.com, without the part before the @'),
  );

export const RequesterRuleConditionSchema = z.discriminatedUnion('attribute', [
  /** Their department is one of these. */
  z.object({ attribute: z.literal('department'), values: ReferenceListSchema }),
  /** Their location is one of these. */
  z.object({ attribute: z.literal('location'), values: ReferenceListSchema }),
  /** Their job title contains this text, ignoring case. */
  z.object({
    attribute: z.literal('job_title'),
    value: z.string().trim().min(1, 'Type part of a job title').max(255),
  }),
  /** Their primary address is at one of these domains. */
  z.object({
    attribute: z.literal('email_domain'),
    values: z.array(EmailDomainSchema).min(1, 'Enter at least one domain').max(REQUESTER_RULE_LIMITS.domains),
  }),
  /** They are, or are not, marked VIP. */
  z.object({ attribute: z.literal('vip'), value: z.boolean() }),
  /** Their account is active, or deactivated. */
  z.object({ attribute: z.literal('active'), value: z.boolean() }),
]);
export type RequesterRuleCondition = z.infer<typeof RequesterRuleConditionSchema>;

/**
 * Who a rule-based group contains: people matching all, or any, of the
 * conditions. Merged and deleted accounts never match, whatever the rule says.
 */
export const RequesterGroupRuleSchema = z.object({
  match: z.enum(['all', 'any']),
  conditions: z
    .array(RequesterRuleConditionSchema)
    .min(1, 'Add at least one condition')
    .max(REQUESTER_RULE_LIMITS.conditions),
});
export type RequesterGroupRule = z.infer<typeof RequesterGroupRuleSchema>;
